import React, { useState, useEffect } from 'react';
import {
  Globe,
  Rocket,
  Paintbrush,
  FileText,
  CreditCard,
  Building2,
  Ship,
  ExternalLink,
  ShieldCheck,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Server,
  Layers,
  Sparkles,
  ArrowLeft,
  Plus,
  Settings,
  Menu as MenuIcon,
  Image as ImageIcon,
  Save,
  TrendingUp,
  Calendar,
  MessageSquare,
  Eye,
  Zap,
  ChevronRight,
  RefreshCw,
  X,
  Send,
  Phone,
  Mail,
  User,
  Loader2,
} from 'lucide-react';
import confetti from 'canvas-confetti';
import { useSite } from '../../context/SiteContext';
import { useAuth } from '../../context/AuthContext';
import { ShopierService } from '../../services/shopierService';
import { getSiteDisplayAddress, getSitePublicUrl } from '../../utils/security';
import { realtimeDbService as dbService } from '../../services/realtimeDbService';
import { SUBSCRIPTION_PLANS } from '../../config/constants';
import {
  YachtItem,
  YachtReservation,
  SiteCommunication,
  SiteAnalytics,
  BlogPost,
  SubscriptionPlanId,
} from '../../types';
import { PromoCard } from '../common/PromoCard';
import { HeaderSettingsModal } from '../editor/HeaderSettingsModal';
import { PageManagerModal } from '../editor/PageManagerModal';
import { ShopierModal } from '../billing/ShopierModal';
import { DomainManager } from '../domains/DomainManager';

interface DashboardOverviewProps {
  onSelectTab?: (tab: string) => void;
  onNavigate?: (tab: string) => void;
  onOpenLiveSite?: () => void;
  onOpenLivePreview?: () => void;
  onOpenShopierModal?: () => void;
  onOpenEditor?: () => void;
  onOpenCreateSite?: () => void;
}

type ManagementSubTab = 'pages' | 'media' | 'menus' | 'seo' | 'domain' | 'publish';

export const DashboardOverview: React.FC<DashboardOverviewProps> = ({
  onSelectTab,
  onNavigate,
  onOpenLiveSite,
  onOpenLivePreview,
  onOpenShopierModal,
  onOpenEditor,
  onOpenCreateSite,
}) => {
  const {
    sites,
    activeSite,
    setActiveSiteId,
    setActivePageId,
    updateSiteData,
    publishCurrentSite,
    isPublishing,
    showToast,
    websiteLimit,
    remainingWebsiteQuota,
    sectorQuotas,
  } = useSite();
  const { currentUser } = useAuth();

  const currentSector = activeSite?.industry === 'YACHT_RENTAL' ? 'YACHT_RENTAL' : 'CORPORATE';
  const currentSectorQuota = sectorQuotas[currentSector];

  const [activeSubTab, setActiveSubTab] = useState<ManagementSubTab>('pages');
  const [isHeaderSettingsOpen, setIsHeaderSettingsOpen] = useState(false);
  const [isPageManagerOpen, setIsPageManagerOpen] = useState(false);
  const [isLocalShopierOpen, setIsLocalShopierOpen] = useState(false);

  // Real business metrics data (NO fake numbers)
  const [yachts, setYachts] = useState<YachtItem[]>([]);
  const [reservations, setReservations] = useState<YachtReservation[]>([]);
  const [communications, setCommunications] = useState<SiteCommunication[]>([]);
  const [analytics, setAnalytics] = useState<SiteAnalytics | null>(null);
  const [blogs, setBlogs] = useState<BlogPost[]>([]);
  const [isLoadingBusinessData, setIsLoadingBusinessData] = useState<boolean>(true);

  // SEO form state
  const [seoTitle, setSeoTitle] = useState(activeSite?.settings?.metaTitle || activeSite?.name || '');
  const [seoDesc, setSeoDesc] = useState(activeSite?.settings?.metaDescription || '');
  const [customDomainInput, setCustomDomainInput] = useState(activeSite?.customDomain || '');
  const [isSavingSettings, setIsSavingSettings] = useState(false);

  useEffect(() => {
    if (!activeSite?.id) return;
    let isMounted = true;
    setIsLoadingBusinessData(true);

    Promise.all([
      dbService.getSiteYachts(activeSite.id),
      dbService.getSiteReservations(activeSite.id),
      dbService.getSiteCommunications(activeSite.id),
      dbService.getSiteAnalytics(activeSite.id),
      dbService.getSiteBlogs(activeSite.id),
    ])
      .then(([y, r, c, a, b]) => {
        if (isMounted) {
          setYachts(y || []);
          setReservations(r || []);
          setCommunications(c || []);
          setAnalytics(a || null);
          setBlogs(b || []);
          setIsLoadingBusinessData(false);
        }
      })
      .catch((err) => {
        console.warn('Dashboard data fetch fallback:', err);
        if (isMounted) setIsLoadingBusinessData(false);
      });

    return () => {
      isMounted = false;
    };
  }, [activeSite?.id]);

  const handleTab = (tab: string) => {
    if (tab === 'editor' && onOpenEditor) {
      onOpenEditor();
      return;
    }
    if (onSelectTab) onSelectTab(tab);
    if (onNavigate) onNavigate(tab);
  };

  const handleLive = () => {
    if (onOpenLivePreview) onOpenLivePreview();
    else if (onOpenLiveSite) onOpenLiveSite();
  };

  if (!activeSite) {
    const handleCreateClick = () => {
      if (onOpenCreateSite) onOpenCreateSite();
      else handleTab('sites');
    };

    return (
      <div className="p-4 md:p-8 space-y-8 max-w-7xl mx-auto select-none">
        {/* 1. Header / Top Greeting & Trial Status */}
        <div className="bg-white border border-slate-200/80 rounded-3xl p-6 md:p-8 shadow-xs flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200 flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 text-blue-600" />
                3 Günlük Ücretsiz Deneme Sürümü Aktif
              </span>
              <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                1 Web Sitesi Hakkı
              </span>
            </div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight">
              Hoş Geldiniz, {currentUser?.displayName || currentUser?.companyName || 'Değerli Girişimci'}
            </h1>
            <p className="text-sm text-slate-500 max-w-2xl leading-relaxed">
              Adiosoft Yönetim Paneline hoş geldiniz. Dakikalar içinde profesyonel, mobil uyumlu ve SEO dostu web sitenizi oluşturup yayına alabilirsiniz.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3 shrink-0">
            <button
              onClick={handleCreateClick}
              className="px-5 py-3 rounded-2xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-sm shadow-md shadow-blue-600/20 transition flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              <span>Web Sitesi Oluştur</span>
              <Sparkles className="w-3.5 h-3.5 text-amber-300" />
            </button>

            <button
              onClick={() => handleTab('billing')}
              className="px-5 py-3 rounded-2xl bg-slate-50 hover:bg-slate-100 border border-slate-300 text-slate-700 font-semibold text-sm transition flex items-center gap-2"
            >
              <CreditCard className="w-4 h-4 text-slate-500" />
              <span>Paketleri İncele</span>
            </button>
          </div>
        </div>

        {/* 2. Existing Sites (if any exists in DB but not set as activeSite yet) */}
        {sites && sites.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-xs space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-base font-bold text-slate-900">Mevcut Web Siteleriniz ({sites.length})</h3>
                <p className="text-xs text-slate-500">Yönetmek istediğiniz siteyi seçerek düzenlemeye devam edin.</p>
              </div>
              <button
                onClick={handleCreateClick}
                className="text-xs font-semibold text-blue-600 hover:text-blue-500 flex items-center gap-1"
              >
                <Plus className="w-3.5 h-3.5" />
                Yeni Ekle
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sites.map((site) => (
                <div
                  key={site.id}
                  className="p-4 rounded-2xl border border-slate-200 bg-slate-50 hover:border-blue-300 hover:bg-white transition flex flex-col justify-between"
                >
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-slate-200 text-slate-700">
                        {site.industry === 'YACHT_RENTAL' ? 'Lüks Yat' : 'Kurumsal'}
                      </span>
                      <span className="text-[11px] font-semibold text-slate-400">
                        {site.status === 'PUBLISHED' ? 'Yayında' : 'Taslak'}
                      </span>
                    </div>
                    <h4 className="font-bold text-slate-900 text-sm truncate">{site.name}</h4>
                    <p className="text-xs text-slate-500 truncate">{getSiteDisplayAddress(site)}</p>
                  </div>
                  <button
                    onClick={() => setActiveSiteId(site.id)}
                    className="mt-4 w-full py-2 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold transition flex items-center justify-center gap-1.5"
                  >
                    <span>Yönet ve Düzenle</span>
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 3. Empty State Hero: Sektör Seçim Bento Kartları */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-slate-900">Sektörünüzü Seçerek Başlayın</h2>
              <p className="text-xs text-slate-500">
                İhtiyacınıza uygun sektörü seçip hazır şablon veya yapay zeka jeneratörü ile hemen kurun.
              </p>
            </div>
            <div className="hidden sm:flex items-center gap-2 text-xs text-slate-500">
              <ShieldCheck className="w-4 h-4 text-emerald-500" />
              <span>Kredi kartı gerekmez</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Kurumsal Kart */}
            <div className="bg-white border-2 border-blue-100 hover:border-blue-400 rounded-3xl p-6 md:p-8 shadow-xs transition flex flex-col justify-between group">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="w-12 h-12 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center group-hover:scale-110 transition">
                    <Building2 className="w-6 h-6" />
                  </div>
                  <span className="px-3 py-1 rounded-full text-xs font-bold bg-blue-50 text-blue-700 border border-blue-200">
                    Deneme Kapsamında
                  </span>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-1">Kurumsal Web Sitesi</h3>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    Şirketler, ajanslar, klinikler, hukuk büroları ve hizmet sunan işletmeler için modern, SEO uyumlu ve hızlı web sitesi.
                  </p>
                </div>

                <div className="space-y-2 pt-2 border-t border-slate-100 text-xs text-slate-600">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                    <span>Hizmetler, blog ve müşteri yorumları modülleri</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                    <span>Yapay zeka ile hazır içerik ve profesyonel tasarım</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                    <span>Özel alan adı (domain) bağlama ve SSL sertifikası</span>
                  </div>
                </div>
              </div>

              <button
                onClick={handleCreateClick}
                className="mt-6 w-full py-3.5 px-4 rounded-2xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-sm shadow-md shadow-blue-600/20 transition flex items-center justify-center gap-2"
              >
                <Plus className="w-4 h-4" />
                <span>Kurumsal Web Sitesi Oluştur</span>
              </button>
            </div>

            {/* Yat Kiralama Kart */}
            <div className="bg-white border-2 border-cyan-100 hover:border-cyan-400 rounded-3xl p-6 md:p-8 shadow-xs transition flex flex-col justify-between group">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="w-12 h-12 rounded-2xl bg-cyan-50 text-cyan-600 flex items-center justify-center group-hover:scale-110 transition">
                    <Ship className="w-6 h-6" />
                  </div>
                  <span className="px-3 py-1 rounded-full text-xs font-bold bg-cyan-50 text-cyan-700 border border-cyan-200">
                    Deneme Kapsamında
                  </span>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-1">Lüks Yat Kiralama & Gulet</h3>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    Yat kiralama acenteleri, gulet sahipleri ve mavi tur operatörleri için rezervasyon ve filo altyapılı web sitesi.
                  </p>
                </div>

                <div className="space-y-2 pt-2 border-t border-slate-100 text-xs text-slate-600">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                    <span>Yat filosu yönetimi, kabin ve teknik özellikler</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                    <span>Mavi tur rotaları, liman rehberi ve takvim</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                    <span>Online rezervasyon talep formu ve WhatsApp entegrasyonu</span>
                  </div>
                </div>
              </div>

              <button
                onClick={handleCreateClick}
                className="mt-6 w-full py-3.5 px-4 rounded-2xl bg-cyan-600 hover:bg-cyan-500 text-white font-bold text-sm shadow-md shadow-cyan-600/20 transition flex items-center justify-center gap-2"
              >
                <Plus className="w-4 h-4" />
                <span>Yat Kiralama Sitesi Oluştur</span>
              </button>
            </div>
          </div>
        </div>

        {/* 4. 3 Adımda Kolay Kurulum Rehberi */}
        <div className="bg-white border border-slate-200 rounded-3xl p-6 md:p-8 shadow-xs">
          <h3 className="text-base font-bold text-slate-900 mb-6 flex items-center gap-2">
            <Zap className="w-4 h-4 text-amber-500" />
            3 Adımda Sitenizi Canlıya Alın
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-4 rounded-2xl bg-slate-50 border border-slate-100 space-y-2">
              <div className="w-8 h-8 rounded-xl bg-blue-600 text-white font-bold text-xs flex items-center justify-center">
                1
              </div>
              <h4 className="font-bold text-slate-900 text-sm">Sektör ve Şablon Seçin</h4>
              <p className="text-xs text-slate-500 leading-relaxed">
                İşletmenize uygun sektörü seçip hazır şablonlarla veya yapay zeka asistanı desteğiyle sitenizi oluşturun.
              </p>
            </div>

            <div className="p-4 rounded-2xl bg-slate-50 border border-slate-100 space-y-2">
              <div className="w-8 h-8 rounded-xl bg-blue-600 text-white font-bold text-xs flex items-center justify-center">
                2
              </div>
              <h4 className="font-bold text-slate-900 text-sm">Görsel Editör ile Özelleştirin</h4>
              <p className="text-xs text-slate-500 leading-relaxed">
                Sürükle bırak editör ile metinlerinizi, görsellerinizi, kurumsal renklerinizi ve menülerinizi kolayca güncelleyin.
              </p>
            </div>

            <div className="p-4 rounded-2xl bg-slate-50 border border-slate-100 space-y-2">
              <div className="w-8 h-8 rounded-xl bg-emerald-600 text-white font-bold text-xs flex items-center justify-center">
                3
              </div>
              <h4 className="font-bold text-slate-900 text-sm">Yayınlayın ve Domain Bağlayın</h4>
              <p className="text-xs text-slate-500 leading-relaxed">
                Tek tıkla sitenizi yayına alın. Dilerseniz kendi özel alan adınızı (ornek.com) anında sitenize yönlendirin.
              </p>
            </div>
          </div>
        </div>

      </div>
    );
  }

  const isYacht = activeSite.industry === 'YACHT_RENTAL';
  const subEval = ShopierService.evaluateSubscriptionStatus(activeSite.subscription?.expireDate || Date.now());

  const storageUsedMB = activeSite.storageUsedMB || 0;
  const storageLimitMB = activeSite.storageLimitMB || (isYacht ? 25000 : 10000);
  const storagePercentage = storageLimitMB > 0 ? Math.min(100, Math.round((storageUsedMB / storageLimitMB) * 100)) : 0;

  const pages = activeSite.pages || [];

  // Quality check calculations
  const hasMetaTitle = Boolean(activeSite.settings?.metaTitle || activeSite.name);
  const hasMetaDesc = Boolean(activeSite.settings?.metaDescription);
  const hasFavicon = Boolean(activeSite.settings?.faviconUrl || activeSite.settings?.logoUrl);
  const hasHomePage = pages.some((p) => p.isHomePage);
  const allPagesHaveSections = pages.every((p) => (p.sections || []).length > 0);

  const qualityScore = Math.min(
    100,
    (hasMetaTitle ? 25 : 0) +
      (hasMetaDesc ? 25 : 0) +
      (hasFavicon ? 15 : 0) +
      (hasHomePage ? 20 : 0) +
      (allPagesHaveSections ? 15 : 0)
  );

  const formatDateTime = (timestamp?: number) => {
    if (!timestamp) return 'Henüz yayınlanmadı';
    try {
      return new Intl.DateTimeFormat('tr-TR', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(timestamp));
    } catch {
      return 'Yakın zamanda';
    }
  };

  const handleSaveSeo = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSavingSettings(true);
    try {
      await updateSiteData({
        settings: {
          ...(activeSite?.settings || {}),
          metaTitle: seoTitle,
          metaDescription: seoDesc,
        },
      });
      showToast('SEO ayarları başarıyla kaydedildi ✓', 'success');
    } catch (err) {
      console.error('SEO save error:', err);
      showToast('Ayarlar kaydedilemedi.', 'error');
    } finally {
      setIsSavingSettings(false);
    }
  };

  const handleSaveDomain = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSavingSettings(true);
    try {
      await updateSiteData({
        customDomain: customDomainInput.trim().toLowerCase(),
      });
      showToast('Özel alan adı kaydedildi ✓', 'success');
    } catch (err) {
      console.error('Domain save error:', err);
      showToast('Alan adı kaydedilemedi.', 'error');
    } finally {
      setIsSavingSettings(false);
    }
  };

  const openRenewModal = () => {
    if (onOpenShopierModal) {
      onOpenShopierModal();
    } else {
      setIsLocalShopierOpen(true);
    }
  };

  const isExpired = subEval.status === 'EXPIRED';
  const isExpiringSoon = subEval.status === 'EXPIRING_SOON';

  const currentPlan =
    (activeSite.subscription?.planId && SUBSCRIPTION_PLANS[activeSite.subscription.planId as SubscriptionPlanId]) ||
    (isYacht ? SUBSCRIPTION_PLANS['PLAN_YACHT_ANNUAL'] : SUBSCRIPTION_PLANS['PLAN_CORP_ANNUAL']);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-6 animate-in fade-in select-none">
      {/* Top Breadcrumb Navigation */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => handleTab('sites')}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-900 transition bg-white px-3 py-1.5 rounded-xl border border-slate-200/80 shadow-xs"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          <span>Tüm Web Sitelerim</span>
        </button>

        <div className="flex items-center gap-2">
          <span className="text-[11px] text-slate-400 font-mono">
            ID: {activeSite.id}
          </span>
        </div>
      </div>

      {/* ==================================================================== */}
      {/* RENEWAL / EXPIRATION BANNER (HIGH VISIBILITY, ACTION-ORIENTED) */}
      {/* ==================================================================== */}
      {isExpired && (
        <div className="p-4 sm:p-5 rounded-2xl bg-rose-50 border border-rose-200/90 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4 animate-in fade-in">
          <div className="flex items-start sm:items-center gap-3.5">
            <div className="w-10 h-10 rounded-xl bg-rose-100 flex items-center justify-center shrink-0 text-rose-600">
              <AlertTriangle className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-sm sm:text-base font-bold text-rose-900">
                  Abonelik Süreniz Sona Erdi ({subEval.formattedExpireDate})
                </h3>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-200 text-rose-800">
                  Yenileme Gerekli
                </span>
              </div>
              <p className="text-xs text-rose-700 mt-0.5 max-w-2xl">
                Web sitenizin yayında kalması ve yeni rezervasyon talepleri almaya devam etmesi için aboneliğinizi yenileyiniz.
                Tüm yat portföyünüz, rotalarınız, sayfalarınız ve medya dosyalarınız güvenle korunmaktadır.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={openRenewModal}
              className="px-4 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-500 text-white font-bold text-xs shadow-sm transition flex items-center gap-2 cursor-pointer"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              <span>Aboneliği Yenile</span>
            </button>
            <button
              type="button"
              onClick={() => handleTab('billing')}
              className="px-3.5 py-2.5 rounded-xl bg-white hover:bg-rose-50 border border-rose-200 text-rose-800 font-semibold text-xs transition cursor-pointer"
            >
              Plan Detayları
            </button>
          </div>
        </div>
      )}

      {!isExpired && (isExpiringSoon || subEval.daysRemaining <= 30) && (
        <div className="p-4 sm:p-5 rounded-2xl bg-amber-50 border border-amber-200/90 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4 animate-in fade-in">
          <div className="flex items-start sm:items-center gap-3.5">
            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center shrink-0 text-amber-700">
              <Clock className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-sm sm:text-base font-bold text-amber-900">
                  Abonelik Yenileme Zamanı ({subEval.daysRemaining} Gün Kaldı)
                </h3>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-200 text-amber-800">
                  Bitiş: {subEval.formattedExpireDate}
                </span>
              </div>
              <p className="text-xs text-amber-700 mt-0.5 max-w-2xl">
                Aboneliğiniz {subEval.formattedExpireDate} tarihinde sona erecektir. Kesintisiz yayın ve rezervasyon akışı için şimdiden sürenizi 1 yıl uzatabilirsiniz.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={openRenewModal}
              className="px-4 py-2.5 rounded-xl bg-amber-600 hover:bg-amber-500 text-white font-bold text-xs shadow-sm transition flex items-center gap-2 cursor-pointer"
            >
              <CreditCard className="w-3.5 h-3.5" />
              <span>Şimdi 1 Yıl Uzat</span>
            </button>
            <button
              type="button"
              onClick={() => handleTab('billing')}
              className="px-3.5 py-2.5 rounded-xl bg-white hover:bg-amber-50 border border-amber-200 text-amber-800 font-semibold text-xs transition cursor-pointer"
            >
              Abonelik Yönetimi
            </button>
          </div>
        </div>
      )}

      {/* ==================================================================== */}
      {/* 1. UNIFIED WEBSITE MANAGEMENT HEADER CARD */}
      {/* ==================================================================== */}
      <div className="p-5 sm:p-7 rounded-3xl bg-white border border-slate-200/90 shadow-xs relative overflow-hidden">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          {/* Main Info */}
          <div className="space-y-3 min-w-0 flex-1">
            {/* Badges: Type & Status */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="px-2.5 py-0.5 rounded-full text-[11px] font-bold tracking-wider uppercase bg-blue-50 text-blue-700 border border-blue-200/60 flex items-center gap-1">
                {isYacht ? <Ship className="w-3.5 h-3.5 text-cyan-600" /> : <Building2 className="w-3.5 h-3.5 text-blue-600" />}
                {isYacht ? 'Yat Kiralama Portalı' : 'Kurumsal Web Sitesi'}
              </span>

              <span
                className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold ${
                  activeSite.status === 'PUBLISHED'
                    ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                    : 'bg-amber-50 text-amber-700 border border-amber-200'
                }`}
              >
                {activeSite.status === 'PUBLISHED' ? '● Canlıda Yayında' : '● Taslak Halinde'}
              </span>
            </div>

            {/* Title */}
            <div>
              <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight truncate">
                {activeSite.name}
              </h1>
            </div>

            {/* Key Specs Row */}
            <div className="flex flex-wrap items-center gap-3 sm:gap-5 text-xs text-slate-600 pt-1">
              {/* Domain */}
              <div className="flex items-center gap-1.5 bg-slate-50 px-2.5 py-1 rounded-lg border border-slate-200/60 font-mono text-[11px]">
                <Globe className="w-3.5 h-3.5 text-slate-400" />
                <span className="font-semibold text-slate-800">
                  {getSiteDisplayAddress(activeSite)}
                </span>
              </div>

              {/* SSL */}
              <div className="flex items-center gap-1 text-emerald-700 font-semibold text-[11px]">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                <span>SSL Aktif (TLS 1.3)</span>
              </div>

              {/* Last Published */}
              <div className="flex items-center gap-1 text-slate-500 text-[11px]">
                <Clock className="w-3.5 h-3.5 text-slate-400" />
                <span>Son Yayın: {formatDateTime(activeSite.lastPublishedAt || activeSite.updatedAt)}</span>
              </div>

              {/* Subscription */}
              <div className="flex items-center gap-1 text-slate-500 text-[11px]">
                <CreditCard className="w-3.5 h-3.5 text-slate-400" />
                <span>{currentSectorQuota?.planName || activeSite.subscription?.planName || (isYacht ? 'Lüks Yat Kiralama Paketi' : 'Kurumsal Web Sitesi Paketi')}</span>
                <span className={currentSectorQuota?.status === 'EXPIRED' ? 'text-rose-600 font-bold' : 'text-slate-400'}>
                  ({currentSectorQuota?.status === 'EXPIRED' ? 'Abonelik Süresi Doldu' : `${subEval.daysRemaining} gün kaldı`})
                </span>
              </div>

              {/* Website Quota Usage */}
              <div
                className={`flex items-center gap-1.5 px-2 py-0.5 rounded-lg border text-[11px] font-semibold ${
                  currentSectorQuota?.status === 'EXPIRED'
                    ? 'bg-rose-50 border-rose-200 text-rose-700'
                    : 'bg-blue-50/70 border-blue-200/60 text-blue-700'
                }`}
              >
                <Layers className="w-3.5 h-3.5" />
                <span>Kullanım: {currentSectorQuota?.used ?? sites.length} / {currentSectorQuota?.total ?? websiteLimit} Website</span>
                <span className="text-[10px] opacity-80">(Kalan: {currentSectorQuota?.remaining ?? remainingWebsiteQuota})</span>
              </div>
            </div>
          </div>

          {/* Primary & Secondary Actions */}
          <div className="flex flex-wrap items-center gap-2.5 shrink-0 self-start lg:self-center">
            {/* [Görsel Editörü Aç] Main Primary Action */}
            <button
              id="btn-open-visual-editor-dashboard"
              type="button"
              onClick={() => handleTab('editor')}
              className="px-5 py-3 rounded-2xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs sm:text-sm shadow-md shadow-blue-500/25 transition flex items-center gap-2"
            >
              <Paintbrush className="w-4 h-4" />
              <span>Görsel Editörü Aç</span>
            </button>

            {/* [Canlı Önizle] */}
            <button
              type="button"
              onClick={handleLive}
              className="px-3.5 py-3 rounded-2xl bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 font-semibold text-xs transition flex items-center gap-1.5 shadow-xs"
            >
              <ExternalLink className="w-4 h-4 text-slate-500" />
              <span>Siteyi Gör</span>
            </button>

            {/* [Canlı Yayınla] */}
            <button
              type="button"
              onClick={publishCurrentSite}
              disabled={isPublishing}
              className="px-3.5 py-3 rounded-2xl bg-slate-900 hover:bg-slate-800 text-white font-semibold text-xs transition flex items-center gap-1.5 shadow-xs disabled:opacity-50"
            >
              <Rocket className="w-4 h-4 text-emerald-400" />
              <span>{isPublishing ? 'Yayınlanıyor...' : 'Yayınla'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* ==================================================================== */}
      {/* 2. REAL COMMERCIAL & SALES METRICS (NO FAKE DATA) */}
      {/* ==================================================================== */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Card 1: Filo / Hizmetler */}
        <div
          onClick={() => handleTab(isYacht ? 'yachts' : 'pages')}
          className="p-5 rounded-2xl bg-white border border-slate-200/90 shadow-xs hover:border-blue-300 transition cursor-pointer group"
        >
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
              {isYacht ? 'Aktif Filo' : 'Hizmet Sayfaları'}
            </span>
            <div className="w-8 h-8 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition">
              {isYacht ? <Ship className="w-4 h-4" /> : <Layers className="w-4 h-4" />}
            </div>
          </div>
          <div className="text-2xl font-extrabold text-slate-900 tracking-tight">
            {isYacht ? `${yachts.length} Lüks Yat` : `${pages.length} Sayfa`}
          </div>
          <div className="text-xs text-slate-500 mt-1 flex items-center justify-between">
            <span>
              {isYacht
                ? yachts.length === 0
                  ? 'Henüz yat eklenmedi'
                  : `${yachts.filter((y) => y.status === 'AVAILABLE').length} Müsait, ${yachts.filter((y) => y.status === 'BOOKED').length} Kirada`
                : `${pages.length} Aktif Sayfa`}
            </span>
            <span className="text-blue-600 font-semibold group-hover:underline flex items-center gap-0.5">
              Yönet <ChevronRight className="w-3 h-3" />
            </span>
          </div>
        </div>

        {/* Card 2: Rezervasyon Talepleri */}
        <div
          onClick={() => handleTab('reservations')}
          className="p-5 rounded-2xl bg-white border border-slate-200/90 shadow-xs hover:border-emerald-300 transition cursor-pointer group"
        >
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
              Rezervasyon Talepleri
            </span>
            <div className="w-8 h-8 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center group-hover:bg-emerald-600 group-hover:text-white transition">
              <Calendar className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-extrabold text-slate-900 tracking-tight">
            {reservations.length} Talep
          </div>
          <div className="text-xs text-slate-500 mt-1 flex items-center justify-between">
            <span>
              {reservations.filter((r) => r.status === 'PENDING').length > 0
                ? `${reservations.filter((r) => r.status === 'PENDING').length} Bekleyen Onay`
                : reservations.length === 0
                ? 'Rezervasyon motoru aktif'
                : 'Tüm talepler işlendi'}
            </span>
            <span className="text-emerald-600 font-semibold group-hover:underline flex items-center gap-0.5">
              İncele <ChevronRight className="w-3 h-3" />
            </span>
          </div>
        </div>

        {/* Card 3: Canlı Trafik & Ziyaretçi */}
        <div
          onClick={() => handleTab('analytics')}
          className="p-5 rounded-2xl bg-white border border-slate-200/90 shadow-xs hover:border-cyan-300 transition cursor-pointer group"
        >
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
              Canlı Ziyaretçi & Trafik
            </span>
            <div className="w-8 h-8 rounded-xl bg-cyan-50 text-cyan-600 flex items-center justify-center group-hover:bg-cyan-600 group-hover:text-white transition">
              <Eye className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-extrabold text-slate-900 tracking-tight">
            {analytics?.totalViews || 0} Görüntülenme
          </div>
          <div className="text-xs text-slate-500 mt-1 flex items-center justify-between">
            <span>{analytics?.uniqueVisitors || 0} Tekil Ziyaretçi</span>
            <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-slate-100 text-slate-600">
              Gerçek Veri
            </span>
          </div>
        </div>

        {/* Card 4: İletişim Mesajları */}
        <div
          onClick={() => handleTab('communications')}
          className="p-5 rounded-2xl bg-white border border-slate-200/90 shadow-xs hover:border-purple-300 transition cursor-pointer group"
        >
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
              İletişim & Teklifler
            </span>
            <div className="w-8 h-8 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center group-hover:bg-purple-600 group-hover:text-white transition">
              <MessageSquare className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-extrabold text-slate-900 tracking-tight">
            {communications.length} Mesaj
          </div>
          <div className="text-xs text-slate-500 mt-1 flex items-center justify-between">
            <span>
              {communications.filter((c) => c.status === 'NEW').length > 0
                ? `${communications.filter((c) => c.status === 'NEW').length} Yeni Cevaplanmamış`
                : 'Tüm mesajlar okundu'}
            </span>
            <span className="text-purple-600 font-semibold group-hover:underline flex items-center gap-0.5">
              Gelen Kutusu <ChevronRight className="w-3 h-3" />
            </span>
          </div>
        </div>
      </div>

      {/* ==================================================================== */}
      {/* 3. ACTIONABLE EMPTY STATES FOR COMMERCIAL ACCELERATION */}
      {/* ==================================================================== */}
      {isYacht && yachts.length === 0 && (
        <div className="p-6 rounded-3xl bg-gradient-to-r from-blue-50 via-cyan-50 to-white border border-blue-200/80 shadow-xs flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-blue-600 text-white flex items-center justify-center shrink-0 shadow-md shadow-blue-500/20">
              <Ship className="w-6 h-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-base font-bold text-slate-900">
                  Yat Kiralama Filonuzu Oluşturun
                </h3>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-700">
                  İlk Adım
                </span>
              </div>
              <p className="text-xs sm:text-sm text-slate-600 mt-1 max-w-2xl">
                Web sitenizde lüks yatlarınızı sergileyerek müşterilerinizin doğrudan rezervasyon formu göndermesini sağlayın.
                Teknik özellikler, kabin sayısı, günlük fiyatlar ve fotoğraflarla filonuzu hemen yayına alın.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => handleTab('yachts')}
            className="px-5 py-3 rounded-2xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs sm:text-sm shadow-md shadow-blue-500/20 transition flex items-center gap-2 whitespace-nowrap cursor-pointer"
          >
            <Plus className="w-4 h-4" />
            <span>Filoya İlk Yatınızı Ekleyin</span>
          </button>
        </div>
      )}

      {reservations.length === 0 && (
        <div className="p-5 rounded-2xl bg-slate-50 border border-slate-200/80 shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3.5">
            <div className="w-10 h-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center shrink-0 text-slate-600">
              <Calendar className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h4 className="text-sm font-bold text-slate-900">
                Online Rezervasyon Motorunuz Aktif
              </h4>
              <p className="text-xs text-slate-500 mt-0.5">
                Müşterileriniz web sitenizdeki yat detay sayfasından veya rezervasyon butonundan tarih seçerek talep gönderdiğinde burada listelenecektir.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleLive}
            className="px-4 py-2 rounded-xl bg-white hover:bg-slate-100 border border-slate-200 text-slate-700 text-xs font-bold transition flex items-center gap-1.5 whitespace-nowrap self-start sm:self-center cursor-pointer"
          >
            <ExternalLink className="w-3.5 h-3.5 text-slate-500" />
            <span>Siteyi Test Et</span>
          </button>
        </div>
      )}

      {blogs.length === 0 && (
        <div className="p-5 rounded-2xl bg-slate-50 border border-slate-200/80 shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3.5">
            <div className="w-10 h-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center shrink-0 text-slate-600">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h4 className="text-sm font-bold text-slate-900">
                Organik Müşteri Kazanımı: SEO Blog Yazısı Ekleyin
              </h4>
              <p className="text-xs text-slate-500 mt-0.5">
                Bodrum, Göcek ve Fethiye mavi tur rotaları hakkında blog içerikleri yayınlayarak Google aramalarında üst sıralara çıkın.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => handleTab('blog')}
            className="px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold transition flex items-center gap-1.5 whitespace-nowrap self-start sm:self-center cursor-pointer"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>İlk Blog Yazısını Yaz</span>
          </button>
        </div>
      )}

      {/* ==================================================================== */}
      {/* 4. DİJİTAL BÜYÜME & AI YEREL GÖRÜNÜRLÜK */}
      {/* ==================================================================== */}
      <div>
        <PromoCard
          id="card-ai-visibility-promo"
          badge={{
            text: 'AI Arama Görünürlüğü',
            icon: Sparkles,
            variant: 'purple',
          }}
          priceBadge="5.000 TL / yıl"
          title="ChatGPT Search & Gemini Varlık Optimizasyonu"
          description="Yapay zeka modellerinin işletmenizi doğru bilgiler, resmi iletişim kanalları ve JSON-LD şemalarıyla tanımasını sağlayın."
          actionText="AI Görünürlük Panelini Aç"
          actionIcon={ChevronRight}
          onAction={() => handleTab('ai-visibility')}
        />
      </div>

      {/* ==================================================================== */}
      {/* 5. UNIFIED MANAGEMENT TABS */}
      {/* ==================================================================== */}
      <div className="bg-white rounded-3xl border border-slate-200/90 shadow-xs overflow-hidden">
        {/* Navigation Tabs Bar */}
        <div className="flex items-center gap-1 p-2 border-b border-slate-100 bg-slate-50/70 overflow-x-auto">
          {[
            { id: 'pages', label: 'Sayfalar', count: pages.length, icon: FileText },
            { id: 'media', label: 'Medya', count: `${storageUsedMB} MB`, icon: ImageIcon },
            { id: 'menus', label: 'Menüler', icon: MenuIcon },
            { id: 'seo', label: 'SEO & Paylaşım', icon: TrendingUp },
            { id: 'domain', label: 'Domain & SSL', icon: Globe },
            { id: 'publish', label: 'Yayınlama & Kalite', count: `%${qualityScore}`, icon: Rocket },
          ].map((tab) => {
            const Icon = tab.icon;
            const isActive = activeSubTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveSubTab(tab.id as ManagementSubTab)}
                className={`px-4 py-2.5 rounded-xl text-xs font-bold transition flex items-center gap-2 whitespace-nowrap ${
                  isActive
                    ? 'bg-white text-blue-600 shadow-xs border border-slate-200/60'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-white/60'
                }`}
              >
                <Icon className={`w-4 h-4 ${isActive ? 'text-blue-600' : 'text-slate-400'}`} />
                <span>{tab.label}</span>
                {tab.count !== undefined && (
                  <span
                    className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                      isActive
                        ? 'bg-blue-50 text-blue-700'
                        : 'bg-slate-200/80 text-slate-600'
                    }`}
                  >
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Tab Body */}
        <div className="p-6">
          {/* TAB 1: SAYFALAR */}
          {activeSubTab === 'pages' && (
            <div className="space-y-5">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-base font-bold text-slate-900">Web Sitesi Sayfaları</h3>
                  <p className="text-xs text-slate-500">
                    Sitede yer alan sayfalar, URL yapıları ve bölüm sayıları.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsPageManagerOpen(true)}
                  className="px-3.5 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold transition flex items-center gap-1.5 shadow-xs"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>Yeni Sayfa Ekle</span>
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
                {pages.map((page) => (
                  <div
                    key={page.id}
                    className="p-4 rounded-2xl bg-slate-50/80 border border-slate-200/80 hover:border-slate-300 transition flex items-center justify-between gap-3"
                  >
                    <div className="min-w-0 space-y-1">
                      <div className="flex items-center gap-2">
                        <h4 className="text-sm font-bold text-slate-900 truncate">{page.title}</h4>
                        {page.isHomePage && (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                            Ana Sayfa
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-slate-500 font-mono">
                        <span>{page.slug}</span>
                        <span>·</span>
                        <span>{(page.sections || []).length} Bölüm</span>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        setActivePageId(page.id);
                        handleTab('editor');
                      }}
                      className="px-3 py-1.5 rounded-xl bg-white hover:bg-slate-100 border border-slate-200 text-slate-700 text-xs font-semibold transition flex items-center gap-1 shadow-2xs"
                    >
                      <Paintbrush className="w-3.5 h-3.5 text-blue-600" />
                      <span>Düzenle</span>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* TAB 2: MEDYA */}
          {activeSubTab === 'media' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-base font-bold text-slate-900">Medya & Depolama</h3>
                <p className="text-xs text-slate-500">
                  Sitenize yüklenen görseller ve bulut depolama kullanım oranı.
                </p>
              </div>

              <div className="p-5 rounded-2xl bg-slate-50 border border-slate-200 space-y-3 max-w-xl">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-bold text-slate-700">Depolama Alanı</span>
                  <span className="font-mono text-slate-500">
                    {storageUsedMB} MB / {(storageLimitMB / 1024).toFixed(0)} GB (%{storagePercentage})
                  </span>
                </div>
                <div className="w-full bg-slate-200 h-2.5 rounded-full overflow-hidden">
                  <div
                    className="bg-blue-600 h-full rounded-full transition-all"
                    style={{ width: `${Math.max(2, storagePercentage)}%` }}
                  />
                </div>
                <p className="text-[11px] text-slate-400">
                  Web sitenizin tüm resimleri WebP formatında otomatik olarak optimize edilir.
                </p>
              </div>

              <div className="pt-2">
                <button
                  type="button"
                  onClick={() => handleTab('editor')}
                  className="px-4 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold transition inline-flex items-center gap-2"
                >
                  <ImageIcon className="w-4 h-4" />
                  <span>Görsel Editörde Medya Ekle</span>
                </button>
              </div>
            </div>
          )}

          {/* TAB 3: MENÜLER */}
          {activeSubTab === 'menus' && (
            <div className="space-y-5">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-base font-bold text-slate-900">Header & Menü Sistemi</h3>
                  <p className="text-xs text-slate-500">
                    Web sitenizin üst menü öğeleri ve link yönlendirmeleri.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsHeaderSettingsOpen(true)}
                  className="px-3.5 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold transition flex items-center gap-1.5 shadow-xs"
                >
                  <Settings className="w-3.5 h-3.5" />
                  <span>Menüleri Düzenle</span>
                </button>
              </div>

              <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-2">
                {(activeSite?.settings?.headerMenu && activeSite.settings.headerMenu.length > 0) ? (
                  activeSite.settings?.headerMenu?.map((item, idx) => (
                    <div
                      key={item.id || idx}
                      className="p-3 bg-white rounded-xl border border-slate-200/80 flex items-center justify-between text-xs"
                    >
                      <div className="flex items-center gap-2">
                        <span className="w-5 h-5 rounded-md bg-slate-100 flex items-center justify-center text-[10px] font-bold text-slate-500">
                          {idx + 1}
                        </span>
                        <span className="font-bold text-slate-800">{item.label}</span>
                      </div>
                      <span className="font-mono text-slate-500 text-[11px]">{item.target || '/'}</span>
                    </div>
                  ))
                ) : (
                  <div className="py-6 text-center text-xs text-slate-500">
                    Menü öğeleri sayfalarınızdan otomatik türetilmektedir. Özelleştirmek için "Menüleri Düzenle" butonuna tıklayın.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* TAB 4: SEO & PAYLAŞIM */}
          {activeSubTab === 'seo' && (
            <form onSubmit={handleSaveSeo} className="space-y-6 max-w-2xl">
              <div>
                <h3 className="text-base font-bold text-slate-900">Arama Motoru Optimizasyonu (SEO)</h3>
                <p className="text-xs text-slate-500">
                  Google ve sosyal medya platformlarında sitenizin nasıl görüneceğini ayarlayın.
                </p>
              </div>

              {/* Google SERP Snippet Preview */}
              <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-1.5 font-sans">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  Google Arama Sonucu Önizlemesi
                </span>
                <div className="text-xs text-slate-600 font-mono">
                  {getSitePublicUrl(activeSite)}
                </div>
                <div className="text-base font-medium text-blue-700 hover:underline cursor-pointer">
                  {seoTitle || activeSite.name}
                </div>
                <div className="text-xs text-slate-600 line-clamp-2">
                  {seoDesc || 'İşletmenizin kurumsal web sitesi ve dijital hizmetleri.'}
                </div>
              </div>

              <div className="space-y-4">
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-800">Meta Başlık (Title)</label>
                  <input
                    type="text"
                    value={seoTitle}
                    onChange={(e) => setSeoTitle(e.target.value)}
                    placeholder="Örn: Apex Global — Stratejik Danışmanlık ve Çözümler"
                    className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-xs bg-white text-slate-900 placeholder:text-slate-400 focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-800">Meta Açıklama (Description)</label>
                  <textarea
                    rows={3}
                    value={seoDesc}
                    onChange={(e) => setSeoDesc(e.target.value)}
                    placeholder="Sitenizi anlatan 150-160 karakterlik açıklama..."
                    className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-xs bg-white text-slate-900 placeholder:text-slate-400 focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSavingSettings}
                  className="px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs shadow-xs transition flex items-center gap-1.5"
                >
                  <Save className="w-3.5 h-3.5" />
                  <span>{isSavingSettings ? 'Kaydediliyor...' : 'SEO Ayarlarını Kaydet'}</span>
                </button>
              </div>
            </form>
          )}

          {/* TAB 5: DOMAIN & SSL */}
          {activeSubTab === 'domain' && (
            <div className="space-y-6">
              <DomainManager onNavigate={onSelectTab} />
            </div>
          )}

          {/* TAB 6: YAYINLAMA & KALİTE */}
          {activeSubTab === 'publish' && (
            <div className="space-y-6 max-w-2xl">
              <div>
                <h3 className="text-base font-bold text-slate-900">Yayınlama ve Kalite Denetimi</h3>
                <p className="text-xs text-slate-600">
                  Web sitenizin canlıya çıkış öncesi kontrol listesi ve sağlık skoru.
                </p>
              </div>

              {/* Score bar */}
              <div className="p-5 rounded-2xl bg-slate-50 border border-slate-200 space-y-3">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-bold text-slate-900">Site Kalite Puanı</span>
                  <span className="font-extrabold text-base text-blue-600">%{qualityScore}</span>
                </div>
                <div className="w-full bg-slate-200 h-2.5 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      qualityScore >= 80 ? 'bg-emerald-500' : 'bg-blue-500'
                    }`}
                    style={{ width: `${qualityScore}%` }}
                  />
                </div>

                <div className="space-y-2.5 pt-2 text-xs text-slate-800">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className={`w-4 h-4 flex-shrink-0 ${hasHomePage ? 'text-emerald-500' : 'text-slate-400'}`} />
                    <span className="font-medium text-slate-800">Ana Sayfa Tanımlı ({hasHomePage ? 'Mevcut' : 'Eksik'})</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className={`w-4 h-4 flex-shrink-0 ${hasMetaTitle ? 'text-emerald-500' : 'text-slate-400'}`} />
                    <span className="font-medium text-slate-800">SEO Meta Başlığı ({hasMetaTitle ? 'Tamam' : 'Eksik'})</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className={`w-4 h-4 flex-shrink-0 ${hasMetaDesc ? 'text-emerald-500' : 'text-slate-400'}`} />
                    <span className="font-medium text-slate-800">SEO Meta Açıklaması ({hasMetaDesc ? 'Tamam' : 'Eksik'})</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className={`w-4 h-4 flex-shrink-0 ${hasFavicon ? 'text-emerald-500' : 'text-slate-400'}`} />
                    <span className="font-medium text-slate-800">Favicon / Logo Görseli ({hasFavicon ? 'Yüklü' : 'Eksik'})</span>
                  </div>
                </div>
              </div>

              <div className="pt-2">
                <button
                  type="button"
                  onClick={publishCurrentSite}
                  disabled={isPublishing}
                  className="px-6 py-3 rounded-2xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs sm:text-sm shadow-md shadow-blue-500/25 transition flex items-center gap-2 disabled:opacity-50"
                >
                  <Rocket className="w-4 h-4" />
                  <span>{isPublishing ? 'Canlıya Yayınlanıyor...' : 'Web Sitesini Canlıya Yayınla'}</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {isLocalShopierOpen && (
        <ShopierModal
          isOpen={isLocalShopierOpen}
          onClose={() => setIsLocalShopierOpen(false)}
          targetPlanId={isYacht ? 'PLAN_YACHT_ANNUAL' : 'PLAN_CORP_ANNUAL'}
        />
      )}

      {isHeaderSettingsOpen && (
        <HeaderSettingsModal
          isOpen={isHeaderSettingsOpen}
          onClose={() => setIsHeaderSettingsOpen(false)}
        />
      )}

      {isPageManagerOpen && (
        <PageManagerModal
          isOpen={isPageManagerOpen}
          onClose={() => setIsPageManagerOpen(false)}
        />
      )}
    </div>
  );
};
